import React from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/Card';
import Button from '../../../ui/Button';
import { LoadingSpinner } from '../../../ui/Loading';
import { VfxSchedule } from '../../../../types';
import { useDeleteVfxSchedule } from '../../../../hooks/useApi';

interface DeleteScheduleModalProps {
  schedule: VfxSchedule | null;
  onClose: () => void;
  onSuccess: () => void;
}

const DeleteScheduleModal: React.FC<DeleteScheduleModalProps> = ({
  schedule,
  onClose,
  onSuccess,
}) => {
  const deleteSchedule = useDeleteVfxSchedule();

  if (!schedule) return null;

  const handleDelete = async () => {
    try {
      await deleteSchedule.mutateAsync(schedule.id);
      onSuccess();
      onClose();
    } catch (error) {
      // Error handled by hook
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 overflow-y-auto overscroll-contain !m-0 !mt-0 p-4 sm:p-6">
      <div className="min-h-full flex items-start justify-center">
        <div className="relative w-full max-w-md my-4 sm:my-8" onClick={(e) => e.stopPropagation()}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-xl font-semibold">Delete Schedule</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-900">Warning</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    This action cannot be undone. The schedule will be permanently deleted.
                  </p>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Schedule Name:</p>
                <p className="font-medium">{schedule.name}</p>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline" onClick={onClose} disabled={deleteSchedule.isLoading}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteSchedule.isLoading}
                  className="flex items-center space-x-2"
                >
                  {deleteSchedule.isLoading ? (
                    <>
                      <LoadingSpinner />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <span>Delete Schedule</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DeleteScheduleModal;

